import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { PartyFormModal } from "../parties/PartyFormModal";
import { OrderAttachmentPanel } from "../attachments/OrderAttachmentPanel";
import { Icon } from "../ui/Icon";
import { GoodsReceiptLinesEditor, emptyGRLine, grLineSktHatasi, type GRLine, type ItemKind } from "./GoodsReceiptLinesEditor";
import { trpc } from "../../lib/trpc";
import { istanbulBugun } from "../../lib/sktKurali";

interface GoodsReceiptProps {
  open: boolean;
  onClose: () => void;
  /** Genel Bakış'tan gelen ön-seçim: ilk kalem tür + ürün dolu açılır. */
  prefill?: { kind: ItemKind; itemId: string } | null;
}

const STOCK_LOC_TYPES = new Set(["depo", "raf", "bolge", "mal_kabul", "karantina", "sevkiyat", "scrap"]);

export function GoodsReceiptDrawer({ open, onClose, prefill }: GoodsReceiptProps) {
  const qc = useQueryClient();
  const [locationId, setLocationId] = useState("");
  /**
   * GİRİŞ NEDENİ — fiş başlığında (bir teslimat tek nedenle girer).
   *  · purchase      → tedarikçiden satın alınan mal (varsayılan).
   *  · opening_stock → SAYIM ÖNCESİ MEVCUT STOK: reçete tüketimi YOK, üretim raporuna
   *                    girmez, ama normal stoktur (FEFO'ya girer, sevk edilir).
   * Açılış seçilince tedarikçi/teslim eden alanları GİZLENİR: açılış stoğunun tedarikçisi
   * yoktur, boş bırakılacak alanları göstermek formu kirletir ve yanlış doldurmaya davet eder.
   */
  const [origin, setOrigin] = useState<"purchase" | "opening_stock">("purchase");
  const [supplierId, setSupplierId] = useState("");
  const [deliveredByName, setDeliveredByName] = useState("");
  const [deliveredByPhone, setDeliveredByPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<GRLine[]>([emptyGRLine()]);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [doneCode, setDoneCode] = useState<string | null>(null);
  const [doneId, setDoneId] = useState<string | null>(null);
  const [supAddOpen, setSupAddOpen] = useState(false);
  /** Kaydet en az bir kez denendi → SKT ihlali üst bantta da ("Kalem N: …") gösterilir. */
  const [denendi, setDenendi] = useState(false);

  const locsQ = trpc.location.list.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const suppliersQ = trpc.supplier.list.useQuery({ page: 1, pageSize: 100 }, { enabled: open });
  const receiptMut = trpc.inventory.createGoodsReceipt.useMutation();

  useEffect(() => {
    if (open) {
      setLocationId(""); setSupplierId(""); setDeliveredByName(""); setDeliveredByPhone(""); setNotes("");
      setLines([prefill ? emptyGRLine({ turKey: `kind:${prefill.kind}`, itemId: prefill.itemId, itemKind: prefill.kind }) : emptyGRLine()]);
      setSubmitErr(null); setDoneCode(null); setDoneId(null); setSupAddOpen(false); setDenendi(false);
      receiptMut.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const locations = (locsQ.data ?? []).filter((l) => l.isActive && STOCK_LOC_TYPES.has(l.type));
  const locationSSOptions = useMemo<SSOption[]>(() => locations.map((l) => ({ value: l.id, label: l.name, tail: l.code })), [locations]);
  const supplierSSOptions = useMemo<SSOption[]>(() => (suppliersQ.data?.rows ?? []).map((s) => ({ value: s.id, label: s.name, tail: s.code })), [suppliersQ.data]);

  /**
   * SKT kuralının (lib/sktKurali.ts — sunucudaki sktTarihHatasi'nin aynası) İLK ihlali, sunucunun
   * createGoodsReceipt metniyle aynı biçimde ("Kalem N: …"). Her çizimde TÜRETİLİR: kullanıcı alanı
   * düzelttiği an bant da kalkar — submitErr'e yazılsaydı düzeltilmiş hata asılı kalırdı.
   * Editörün alan-altı hatası ile aynı kural fonksiyonunu (grLineSktKurali → grLineSktHatasi) kullanır.
   */
  const sktBugun = istanbulBugun();
  let ilkSktHatasi: string | null = null;
  for (const [i, ln] of lines.entries()) {
    const h = grLineSktHatasi(ln, { mode: "create", origin }, sktBugun);
    if (h) { ilkSktHatasi = `Kalem ${i + 1}: ${h}`; break; }
  }

  const errorMessage = useMemo(() => {
    if (submitErr) return submitErr;
    if (denendi && ilkSktHatasi) return ilkSktHatasi;
    // Sunucunun BAD_REQUEST'i (SKT dahil — sunucu kuralı esastır) burada görünür kalır.
    const e = receiptMut.error;
    if (!e) return null;
    const zod = (e.data as { zodError?: { fieldErrors?: Record<string, string[]> } } | undefined)?.zodError?.fieldErrors;
    if (zod) return Object.values(zod).flat().filter(Boolean).join(" · ");
    return e.message;
  }, [submitErr, denendi, ilkSktHatasi, receiptMut.error]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitErr(null);
    setDenendi(true);
    if (!locationId) return setSubmitErr("Lokasyon seçin.");
    const prepared: Array<{ itemKind: ItemKind; itemId: string; locationId: string; qty: number; producedAt?: string; expiresAt?: string; supplierLot?: string; costPerUnit?: number }> = [];
    for (const [i, ln] of lines.entries()) {
      if (!ln.turKey || !ln.itemId || !ln.itemKind) return setSubmitErr(`Kalem ${i + 1}: tür ve ürün seçin.`);
      if (!ln.qty || Number(ln.qty) <= 0) return setSubmitErr(`Kalem ${i + 1}: geçerli miktar girin.`);
      // SKT kuralı ihlali → KAYDETME. Mesaj türetilmiş bantta (ilkSktHatasi) + alanın altında görünür.
      // Ekrandaki durumla aynı "bugün" (sktBugun): gece yarısı dönümünde mesajsız engel olmasın —
      // ekran geçerli diyorsa sunucu karar verir ve kendi mesajını döner.
      if (grLineSktHatasi(ln, { mode: "create", origin }, sktBugun)) return;
      prepared.push({
        itemKind: ln.itemKind, itemId: ln.itemId, locationId, qty: Number(ln.qty),
        producedAt: ln.producedAt ? new Date(ln.producedAt).toISOString() : undefined,
        expiresAt: ln.expiresAt ? new Date(ln.expiresAt).toISOString() : undefined,
        supplierLot: ln.supplierLot || undefined,
        // Fiyat YALNIZ satın almada gönderilir: açılış stoğu satın alma değildir, tedarikçiye
        // borç doğurmaz — alan gizli olsa bile eski bir değer kalmışsa süzülür.
        costPerUnit: origin === "purchase" && ln.costPerUnit ? Number(ln.costPerUnit.replace(",", ".")) : undefined,
      });
    }
    try {
      const res = await receiptMut.mutateAsync({
        origin,
        supplierId: origin === "opening_stock" ? undefined : (supplierId || undefined),
        deliveredByName: deliveredByName || undefined,
        deliveredByPhone: deliveredByPhone || undefined,
        notes: notes || undefined,
        lines: prepared,
      });
      await qc.invalidateQueries();
      setDoneCode(res.code);
      setDoneId(res.id);
    } catch {
      /* err banner */
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      widthPx={1296}
      title="Mal Kabul"
      subtitle="Çok kalemli mal kabul fişi — teslim alınan ürünleri kaydet"
      footer={
        doneCode ? (
          <button type="button" className="btn-md primary" onClick={onClose}>Kapat</button>
        ) : (
          <>
            <button type="button" className="btn-md ghost" onClick={onClose} disabled={receiptMut.isPending}>İptal</button>
            <button type="submit" form="receipt-form" className="btn-md primary" disabled={receiptMut.isPending}>
              {receiptMut.isPending ? "Kaydediliyor…" : "Mal Kabul Et"}
            </button>
          </>
        )
      }
    >
      {doneCode ? (
        <>
          <div className="form-banner-ok">
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Mal kabul tamamlandı</div>
              <div>Fiş no: <span className="mono" style={{ fontWeight: 600 }}>{doneCode}</span> · {lines.length} kalem</div>
              <div style={{ marginTop: 4, fontSize: 12 }}>Stok lokasyona eklendi ve hareketler loglandı. Aşağıdan irsaliye/belge/görsel ekleyebilirsiniz — mal kabul detayında ve tutanakta görünür.</div>
            </div>
          </div>
          {doneId ? (
            <OrderAttachmentPanel
              goodsReceiptId={doneId}
              canEdit
              uploadCategory="receipt"
              title="Belgeler & Görseller"
              subtitle="İrsaliye / belge / görseller — mal kabul fişine bağlanır"
            />
          ) : null}
        </>
      ) : (
        <form id="receipt-form" onSubmit={handleSubmit} noValidate>
          {errorMessage ? <div className="form-banner-err" role="alert"><Icon name="alertTriangle" size={14} />{errorMessage}</div> : null}

          <div className="form-row">
            <div className="form-field">
              <label className="lbl" htmlFor="gr-loc">Lokasyon <span className="req">*</span></label>
              <SearchSelect id="gr-loc" value={locationId || null} options={locationSSOptions} onChange={(v) => setLocationId(v ?? "")} placeholder="— Depo / stok lokasyonu —" searchPlaceholder="Lokasyon ara…" />
              <span className="help">Tüm kalemler bu lokasyona girer.</span>
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="gr-origin">Giriş Nedeni <span className="req">*</span></label>
              <select id="gr-origin" className="form-select" value={origin} onChange={(e) => setOrigin(e.target.value as typeof origin)}>
                <option value="purchase">Satın alma — tedarikçiden mal geldi</option>
                <option value="opening_stock">Sayım / Açılış stoğu — mevcut stok sisteme giriliyor</option>
              </select>
              <span className="help">
                {origin === "opening_stock"
                  ? "Reçete tüketimi YAPILMAZ, üretim raporuna girmez. Normal stoktur: sevk edilebilir."
                  : "Tedarikçiden gelen mal. Reçete tüketimi zaten yapılmaz."}
              </span>
            </div>
          </div>

          {origin === "purchase" ? (
            <>
              <div className="form-row">
                <div className="form-field">
                  <label className="lbl" htmlFor="gr-sup" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span>Tedarikçi</span>
                    <button type="button" className="btn" style={{ height: 22, padding: "0 8px", fontSize: 11, fontWeight: 600, color: "var(--primary)" }} onClick={() => setSupAddOpen(true)}>+ Yeni Tedarikçi</button>
                  </label>
                  <SearchSelect id="gr-sup" value={supplierId || null} options={supplierSSOptions} onChange={(v) => setSupplierId(v ?? "")} placeholder="— Tedarikçi (opsiyonel) —" searchPlaceholder="Tedarikçi ara…" />
                </div>
                <div className="form-field">
                  <label className="lbl" htmlFor="gr-delname">Teslim Eden (Ad Soyad)</label>
                  <input id="gr-delname" className="form-input" type="text" value={deliveredByName} onChange={(e) => setDeliveredByName(e.target.value)} maxLength={120} placeholder="Malı getiren kişi" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label className="lbl" htmlFor="gr-delphone">Teslim Eden Telefon</label>
                  <input id="gr-delphone" className="form-input" type="tel" value={deliveredByPhone} onChange={(e) => setDeliveredByPhone(e.target.value)} maxLength={30} placeholder="05xx xxx xx xx" inputMode="tel" />
                </div>
                <div className="form-field" />
              </div>
            </>
          ) : null}

          <div style={{ marginTop: 12 }}>
            <GoodsReceiptLinesEditor lines={lines} setLines={setLines} enabled={open} origin={origin} mode="create" />
          </div>

          <div className="form-field" style={{ marginTop: 14 }}>
            <label className="lbl" htmlFor="gr-notes">Notlar</label>
            <textarea id="gr-notes" className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} rows={2} placeholder="İrsaliye no, açıklama…" />
          </div>
        </form>
      )}

      <PartyFormModal open={supAddOpen} kind="supplier" onClose={() => setSupAddOpen(false)} onCreated={(s) => setSupplierId(s.id)} />
    </Drawer>
  );
}
